// SidebarHost — projects the shell model into the pure Sidebar (pen "Left
// menu – detail design", decided 2026-10-20).
//
// The composition root of the menu: it registers what works today, on behalf
// of the modules (the Sidebar itself imports none):
//   - menu entries: Projects, Library, Knowledge bank, Journal (Audiences
//     and Curricula join as their views exist). There is no Studio entry:
//     the Studio is a project's canvas, opened from a project, and Projects
//     stays selected while the author is in one;
//   - recent items: the projects (useProjectItems.ts) in pen's list pattern
//     (pins, sort and density per device: useRecentList), then "Show all
//     projects"; the open project is marked in every view. No conversations:
//     the chat is a contextual panel, and its conversations live in its
//     picker (pen, Chat panel Decided 01). The registration stays generic,
//     so another module can register a list here;
//   - foot rows: none yet (Give feedback and Events join when those modules
//     exist; hidden until then).
// The school switcher shows the workspace: one per account today, with no
// way to switch, so it is the single-institution form (a name, not a button).
// The account row opens the account menu (Settings, theme, language, sign
// out); About lives in Settings.
//
// The menu's width is the shared `frame.nav` state (toggle-rail), remembered
// per device (railPreference.ts). Collapsed, it is the icon rail plus the peek:
// the full menu laid over the content while the rail is hovered (pen I-13),
// pure CSS. The rail shows no projects; they appear only in the peek (owner,
// 2026-10-22).
//
// The connection popover (J-05) shows no developer info: its one link,
// "Copy error details", copies the error ID, version and time (SF-14).

export { SidebarHost };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { ConnectionIndicator } from "../../../components/Health/ConnectionIndicator.js";
import { RemoveProjectDialog } from "../../../components/Projects/RemoveProjectDialog.js";
import {
    Sidebar,
    SidebarItem,
    type SidebarLayout,
} from "../../../components/Sidebar/Sidebar.js";
import { SidebarAccount } from "../../../components/Sidebar/SidebarAccount.js";
import {
    type SidebarEntry,
    schoolSwitcherOf,
} from "../../../components/Sidebar/sidebarEntries.js";
import { VersionLabel } from "../../../components/VersionLabel/VersionLabel.js";
import { isLocale, setLocale } from "../../../i18n/setup.js";
import { endonym, locales } from "../../../i18n/supportedLocales.js";
import { buildInfo } from "../../buildInfo.js";
import { useCopy } from "../../copy.js";
import { externalErrorId } from "../../errorId.js";
import { mkUnreachable } from "../../externalError.js";
import { effectiveTheme, useTheme } from "../../theme.js";
import { useAppShell } from "../context.js";
import { accountIdentityOf } from "./accountIdentity.js";
import { useProjectItems } from "./useProjectItems.js";

const SidebarHost = (): ReactNode => {
    const { t, i18n } = useLingui();
    const copy = useCopy();
    const { theme, setTheme } = useTheme();
    const {
        tabs,
        frame,
        dispatch,
        kbdPlatform,
        openOverlay,
        gotoHome,
        gotoKnowledge,
        gotoSearch,
        gotoSettings,
        authStatus,
        displayName,
        library,
        workspaceName,
        signOut,
        health,
        healthReason,
        nextRetryAt,
        retryNow,
    } = useAppShell();
    const expanded = frame.nav === "expanded";
    const gotoProjects = () => dispatch({ type: "open-projects" });
    const active = tabs.active;
    const project = useProjectItems();
    // The Projects entry goes back into the project the author is in (its
    // Studio) from anywhere else; from the Studio itself, or with no live
    // project chosen, it shows all projects.
    const inProject =
        frame.studioProject !== null &&
        project.items.some((item) => item.id === frame.studioProject);
    const openProjects =
        inProject && active.type !== "studio"
            ? () => dispatch({ type: "open-studio" })
            : gotoProjects;

    const menu: readonly SidebarEntry[] = [
        {
            type: "menu-entry",
            id: "projects",
            label: t`Projects`,
            icon: "layout-grid",
            works: true,
            current: active.type === "projects" || active.type === "studio",
            open: openProjects,
        },
        {
            type: "menu-entry",
            id: "library",
            label: t`Library`,
            icon: "library",
            works: true,
            current: active.type === "search",
            open: () => gotoSearch(),
        },
        {
            type: "menu-entry",
            id: "knowledge",
            label: t`Knowledge bank`,
            icon: "knowledge",
            works: true,
            current: active.type === "knowledge",
            open: gotoKnowledge,
        },
        {
            type: "menu-entry",
            id: "journal",
            label: t`Journal`,
            icon: "compose",
            works: true,
            current: active.type === "home",
            open: gotoHome,
        },
    ];

    const projects: readonly SidebarEntry[] = project.items;

    const identity =
        authStatus?._tag === "authenticated"
            ? {
                  ...accountIdentityOf(displayName, authStatus.user.email),
                  email: authStatus.user.email,
              }
            : null;

    // The workspace stands in for the institution until institutions exist;
    // shown once the library has loaded (its name comes with it).
    const school =
        library === null
            ? schoolSwitcherOf([], "", undefined)
            : schoolSwitcherOf(
                  [
                      {
                          id: "workspace",
                          name:
                              workspaceName === ""
                                  ? t`Unnamed workspace`
                                  : workspaceName,
                      },
                  ],
                  "workspace",
                  undefined,
              );

    const copyVersion =
        copy === null ? undefined : () => copy({ type: "version" });
    // The connection trouble's reference: the server could not be reached.
    const copyConnectionDetails =
        copy === null
            ? undefined
            : () =>
                  copy({
                      type: "error-info",
                      errorId: externalErrorId(mkUnreachable()),
                  });

    const sidebarIn = (layout: SidebarLayout): ReactNode => {
        const labelled = layout !== "rail";
        return (
            <Sidebar
                layout={layout}
                onToggle={() => dispatch({ type: "toggle-rail" })}
                school={school}
                onNewProject={project.newProject}
                onOpenSearch={() => openOverlay({ type: "command" })}
                platform={kbdPlatform}
                entries={[...menu, ...projects]}
                recentLists={[
                    {
                        id: "projects",
                        view: project.view,
                        onCommand: project.onCommand,
                        labels: {
                            pinned: t`Pinned projects`,
                            recent: t`Recent projects`,
                        },
                        showAll: {
                            label: t`Show all projects`,
                            open: gotoProjects,
                        },
                        loading: false,
                    },
                ]}
                account={
                    // Before the auth probe resolves: Settings stays one row.
                    identity === null ? (
                        <SidebarItem
                            label={t`Settings`}
                            icon="gear"
                            current={active.type === "settings"}
                            expanded={labelled}
                            onSelect={gotoSettings}
                        />
                    ) : (
                        <SidebarAccount
                            name={identity.name}
                            email={identity.email}
                            initials={identity.initials}
                            expanded={labelled}
                            current={active.type === "settings"}
                            onSettings={gotoSettings}
                            theme={effectiveTheme(theme)}
                            onTheme={setTheme}
                            languages={locales.map((l) => ({
                                value: l,
                                label: endonym(l),
                            }))}
                            language={i18n.locale}
                            onLanguage={(l) => {
                                if (isLocale(l)) setLocale(l);
                            }}
                            onSignOut={signOut}
                        />
                    )
                }
                connection={
                    <ConnectionIndicator
                        health={health}
                        reason={healthReason}
                        nextRetryAt={nextRetryAt}
                        variant={labelled ? "pill" : "icon"}
                        onRetryNow={retryNow}
                        onCopyDetails={copyConnectionDetails}
                    />
                }
                version={
                    <VersionLabel
                        version={buildInfo.version}
                        short={!labelled}
                        onCopy={copyVersion}
                    />
                }
            />
        );
    };

    // "Delete …" on a project in the menu asks first, here.
    const removal = <RemoveProjectDialog {...project.removal} />;
    return expanded ? (
        <>
            {sidebarIn("expanded")}
            {removal}
        </>
    ) : (
        <div className="bilda-sidebar-host">
            {sidebarIn("rail")}
            <div className="bilda-sidebar-peek">{sidebarIn("peek")}</div>
            {removal}
        </div>
    );
};
